<template>
  <div class="tw:my-4">
    <div v-if="isDescriptionsError">
      <p class="tw:text-center tw:text-xl">
        {{ $t('onboarding.appLaunchError.header') }}
      </p>
    </div>

    <div v-else class="tw:grid tw:grid-cols-1 tw:gap-8 tw:md:grid-cols-[minmax(0,1fr)_332px]">
      <div class="tw:min-w-0">
        <CardLoadingSkeleton v-if="isDescriptionsLoading" class="tw:w-full" height="360px" />
        <BaseCard v-else>
          <PropertyDescriptionEmptyState v-if="!hasDescriptions" @languageSelected="addDescription" />
          <div v-else>
            <div class="tw:mb-5 tw:flex tw:flex-col tw:gap-1">
              <h2 class="tw:text-xl tw:font-bold tw:text-gray-800">{{ $t('description_header') }}</h2>
              <p class="tw:text-sm tw:text-gray-800">{{ $t('description_subtext') }}</p>
            </div>

            <div class="tw:flex tw:flex-col tw:gap-5">
              <PropertyDescriptionCard
                v-for="description in descriptions"
                :key="description.languageCode"
                :description="description"
                @edit="editDescription" />
            </div>

            <PropertyDescriptionAddMoreLanguages @languageSelected="addDescription" />
          </div>
        </BaseCard>
      </div>

      <div class="tw:w-full">
        <PropertyDescriptionSidebar />
      </div>
    </div>

    <PropertyDescriptionFormModal
      :isOpen="isDescriptionModalOpen"
      :isEdit="isEditingDescription"
      :description="selectedDescription"
      :language="selectedLanguage"
      :draftDescription="draftDescription"
      :hasError="hasDescriptionFormError"
      :isSaving="isSavePropertyDescriptionLoading"
      @close="closeDescriptionModal"
      @close-with-changes="openUnsavedChangesConfirmation"
      @submit="submitDescription"
      @remove="removeDescription" />

    <PropertyDescriptionUnsavedChangesModal
      :isOpen="isUnsavedChangesConfirmationOpen"
      :isSaving="isSavePropertyDescriptionLoading"
      @close="returnToPendingDescriptionModal"
      @discard="closeUnsavedChangesConfirmation"
      @save="saveUnsavedChanges" />

    <PropertyDescriptionRemoveConfirmationModal
      :isOpen="isRemoveConfirmationOpen"
      :previewText="selectedDescription?.description"
      :previewTextDirection="getDescriptionTextDirection(selectedDescription?.languageCode)"
      :isRemoving="isDeletePropertyDescriptionLoading"
      @close="closeRemoveConfirmation"
      @goBack="goBackToEditDescription"
      @remove="confirmRemoveDescription" />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

import BaseCard from '@/components/dashboard/BaseCard.vue'
import CardLoadingSkeleton from '@/components/loadingSkeletons/CardLoadingSkeleton.vue'
import useToastNotifications from '@/components/notifications/useToastNotifications'
import PropertyDescriptionCard from '@/components/propertyDetails/propertyDescriptions/PropertyDescriptionCard.vue'
import PropertyDescriptionEmptyState from '@/components/propertyDetails/propertyDescriptions/PropertyDescriptionEmptyState.vue'
import PropertyDescriptionSidebar from '@/components/propertyDetails/propertyDescriptions/PropertyDescriptionSidebar.vue'
import PropertyDescriptionAddMoreLanguages from '@/components/propertyDetails/propertyDescriptions/PropertyDescriptionAddMoreLanguages.vue'
import PropertyDescriptionFormModal from '@/components/propertyDetails/propertyDescriptions/PropertyDescriptionFormModal.vue'
import PropertyDescriptionRemoveConfirmationModal from '@/components/propertyDetails/propertyDescriptions/PropertyDescriptionRemoveConfirmationModal.vue'
import PropertyDescriptionUnsavedChangesModal from '@/components/propertyDetails/propertyDescriptions/PropertyDescriptionUnsavedChangesModal.vue'
import { selectedAccommodationId } from '@/layouts/queries'
import {
  hasDescriptionValidationError,
  useCreatePropertyDescription,
  useDeletePropertyDescription,
  useUpdatePropertyDescription,
} from '@/components/propertyDetails/propertyDescriptions/queries'
import { usePropertyDescriptions } from '@/components/propertyDetails/propertyDescriptions/usePropertyDescriptions.js'

const {
  descriptions,
  hasDescriptions,
  getLanguageLabelByCode,
  getDescriptionTextDirection,
  isDescriptionsLoading,
  isDescriptionsError,
} =
  usePropertyDescriptions()
const { deletePropertyDescription, isDeletePropertyDescriptionLoading } = useDeletePropertyDescription()
const { updatePropertyDescription, isUpdatePropertyDescriptionLoading } = useUpdatePropertyDescription()
const { createPropertyDescription, isCreatePropertyDescriptionLoading } = useCreatePropertyDescription()
const { displayNotification, showSaveError } = useToastNotifications()

const isSavePropertyDescriptionLoading = computed(
  () => isUpdatePropertyDescriptionLoading.value || isCreatePropertyDescriptionLoading.value
)

const isDescriptionModalOpen = ref(false)
const isEditingDescription = ref(false)
const isRemoveConfirmationOpen = ref(false)
const isUnsavedChangesConfirmationOpen = ref(false)
const selectedDescription = ref(null)
const selectedLanguage = ref(null)
const pendingDescriptionChanges = ref(null)
const draftDescription = ref(null)
const hasDescriptionFormError = ref(false)

const addDescription = language => {
  clearDescriptionFormError()
  draftDescription.value = null
  isEditingDescription.value = false
  selectedLanguage.value = language
  selectedDescription.value = null
  isDescriptionModalOpen.value = true
}

const editDescription = description => {
  clearDescriptionFormError()
  draftDescription.value = null
  isEditingDescription.value = true
  selectedDescription.value = description
  selectedLanguage.value = null
  isDescriptionModalOpen.value = true
}

const closeDescriptionModal = () => {
  isDescriptionModalOpen.value = false
  draftDescription.value = null
  clearDescriptionFormError()
}

const openUnsavedChangesConfirmation = payload => {
  pendingDescriptionChanges.value = payload
  isDescriptionModalOpen.value = false
  isUnsavedChangesConfirmationOpen.value = true
}

const closeUnsavedChangesConfirmation = () => {
  isUnsavedChangesConfirmationOpen.value = false
  pendingDescriptionChanges.value = null
  draftDescription.value = null
}

const returnToDescriptionModal = ({ description }) => {
  draftDescription.value = description
  isUnsavedChangesConfirmationOpen.value = false
  pendingDescriptionChanges.value = null
  isDescriptionModalOpen.value = true
}

const returnToPendingDescriptionModal = () => {
  if (!pendingDescriptionChanges.value) {
    return
  }

  returnToDescriptionModal(pendingDescriptionChanges.value)
}

const clearDescriptionFormError = () => {
  hasDescriptionFormError.value = false
}

const handleDescriptionSaveError = ({ error, payload, shouldReturnToForm = false }) => {
  if (shouldReturnToForm) {
    returnToDescriptionModal(payload)
  }

  if (hasDescriptionValidationError(error)) {
    hasDescriptionFormError.value = true
    return
  }

  showSaveError()
}

const getDescriptionLanguageLabel = ({ descriptionDetails, language }) => {
  const languageCode = descriptionDetails?.languageCode ?? language?.language

  if (!languageCode) {
    return '-'
  }

  return getLanguageLabelByCode(languageCode)
}

const displaySuccessNotification = ({ message, languageLabel }) => {
  displayNotification({
    message,
    isTranslationKey: true,
    isHtml: false,
    type: 'success',
    translationParams: {
      Language: languageLabel,
    },
  })
}

const displayDescriptionSaveSuccess = payload => {
  displaySuccessNotification({
    message: payload.isEdit ? 'description_updated_success' : 'description_added_success',
    languageLabel: getDescriptionLanguageLabel(payload),
  })
}

const updateDescription = (
  { isEdit, descriptionDetails, language, description },
  onSuccess,
  shouldReturnToFormOnError = false
) => {
  clearDescriptionFormError()

  const languageCode = descriptionDetails?.languageCode ?? language?.language
  const payload = { isEdit, descriptionDetails, language, description }

  updatePropertyDescription(
    {
      accommodationId: selectedAccommodationId.value,
      languageCode,
      description,
    },
    {
      onSuccess: () => {
        displayDescriptionSaveSuccess(payload)
        onSuccess()
      },
      onError: error =>
        handleDescriptionSaveError({
          error,
          payload,
          shouldReturnToForm: shouldReturnToFormOnError,
        }),
    }
  )
}

const createDescription = (
  { isEdit, descriptionDetails, language, description },
  onSuccess,
  shouldReturnToFormOnError = false
) => {
  clearDescriptionFormError()

  const languageCode = language?.language
  const payload = { isEdit, descriptionDetails, language, description }

  createPropertyDescription(
    {
      accommodationId: selectedAccommodationId.value,
      languageCode,
      description,
    },
    {
      onSuccess: () => {
        displayDescriptionSaveSuccess(payload)
        onSuccess()
      },
      onError: error =>
        handleDescriptionSaveError({
          error,
          payload,
          shouldReturnToForm: shouldReturnToFormOnError,
        }),
    }
  )
}

const submitDescription = payload => {
  if (payload.isEdit) {
    updateDescription(payload, closeDescriptionModal)
    return
  }

  createDescription(payload, closeDescriptionModal)
}

const saveUnsavedChanges = () => {
  if (pendingDescriptionChanges.value) {
    if (pendingDescriptionChanges.value.isEdit) {
      updateDescription(pendingDescriptionChanges.value, closeUnsavedChangesConfirmation, true)
      return
    }

    createDescription(pendingDescriptionChanges.value, closeUnsavedChangesConfirmation, true)
    return
  }

  closeUnsavedChangesConfirmation()
}

const removeDescription = () => {
  isDescriptionModalOpen.value = false
  isRemoveConfirmationOpen.value = true
}

const goBackToEditDescription = () => {
  isRemoveConfirmationOpen.value = false
  isEditingDescription.value = true
  isDescriptionModalOpen.value = true
}

const closeRemoveConfirmation = () => {
  isRemoveConfirmationOpen.value = false
}

const confirmRemoveDescription = () => {
  const descriptionToRemove = selectedDescription.value
  if (!descriptionToRemove) {
    return
  }

  deletePropertyDescription(
    {
      accommodationId: selectedAccommodationId.value,
      languageCode: descriptionToRemove.languageCode,
    },
    {
      onSuccess: () => {
        displaySuccessNotification({
          message: 'description_removed_success',
          languageLabel: getDescriptionLanguageLabel({ descriptionDetails: descriptionToRemove }),
        })
        isRemoveConfirmationOpen.value = false
        selectedDescription.value = null
      },
      onError: showSaveError,
    }
  )
}
</script>
